import { useEffect, useRef, useState, type CSSProperties, type KeyboardEvent, type ReactNode } from "react";

export type SelectOption<T extends string = string> = {
  value: T;
  label: string;
  /** Optional leading node (flag, icon, color dot). */
  left?: ReactNode;
  /**
   * Optional heading this option sits under, the <optgroup> equivalent. Options
   * carrying the same group render beneath one header, in list order; options
   * without a group render ungrouped above the first header.
   */
  group?: string;
};

type Props<T extends string> = {
  value: T;
  options: SelectOption<T>[];
  onChange: (value: T) => void;
  disabled?: boolean;
  placeholder?: string;
  ariaLabel?: string;
  /** Applied to the wrapper — use for width / margins. */
  style?: CSSProperties;
};

/**
 * App-styled replacement for native <select>. The closed control looks like an
 * `.input`; the open list is a custom popover (var(--surface)/var(--border)) so
 * it matches the rest of the UI instead of the OS dropdown. Keyboard + outside
 * click supported. Use this for every fixed-option dropdown going forward.
 */
export function Select<T extends string>({
  value, options, onChange, disabled, placeholder, ariaLabel, style,
}: Props<T>) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);
  const selected = options.find((o) => o.value === value) ?? null;

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  // Highlight the current value each time the list opens.
  useEffect(() => {
    if (!open) return;
    const i = options.findIndex((o) => o.value === value);
    setActive(i >= 0 ? i : 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function choose(i: number) {
    const opt = options[i];
    if (!opt) return;
    onChange(opt.value);
    setOpen(false);
  }

  function onKeyDown(e: KeyboardEvent<HTMLButtonElement>) {
    if (disabled) return;
    switch (e.key) {
      case "Enter":
      case " ":
        e.preventDefault();
        if (open) choose(active); else setOpen(true);
        break;
      case "Escape":
        setOpen(false);
        break;
      case "ArrowDown":
        e.preventDefault();
        if (!open) { setOpen(true); break; }
        setActive((a) => Math.min(options.length - 1, a + 1));
        break;
      case "ArrowUp":
        e.preventDefault();
        if (!open) { setOpen(true); break; }
        setActive((a) => Math.max(0, a - 1));
        break;
    }
  }

  return (
    <div ref={wrapRef} style={{ position: "relative", ...style }}>
      <button
        type="button"
        className="input"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => { if (!disabled) setOpen((o) => !o); }}
        onKeyDown={onKeyDown}
        style={trigger}
      >
        <span style={{ ...triggerLabel, color: selected ? "var(--ink)" : "var(--ink-3)" }}>
          {selected?.left && <span style={levo}>{selected.left}</span>}
          {selected ? selected.label : (placeholder ?? "Select…")}
        </span>
        <svg width="10" height="6" viewBox="0 0 10 6" aria-hidden
          style={{ flex: "none", transform: open ? "rotate(180deg)" : "none", transition: "transform .12s", color: "var(--ink-3)" }}>
          <path fill="none" stroke="currentColor" strokeWidth="1.5" d="M1 1l4 4 4-4" />
        </svg>
      </button>

      {open && (
        <div role="listbox" className="dd-scroll" style={panel}>
          {options.map((o, i) => {
            const isSel = o.value === value;
            // First option of a new group gets the header above it.
            const header = o.group && o.group !== options[i - 1]?.group ? o.group : null;
            return (
              <div key={o.value}>
                {header && <div style={groupHead}>{header}</div>}
              <button
                type="button"
                role="option"
                aria-selected={isSel}
                onMouseEnter={() => setActive(i)}
                onClick={() => choose(i)}
                style={{ ...option, background: i === active ? "var(--surface-2)" : "transparent", fontWeight: isSel ? 600 : 400 }}
              >
                {o.left && <span style={levo}>{o.left}</span>}
                <span style={{ flex: 1 }}>{o.label}</span>
                {isSel && (
                  <svg width="13" height="13" viewBox="0 0 24 24" aria-hidden style={{ flex: "none", color: "var(--brand-primary)" }}>
                    <path fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" d="M5 12l5 5L20 7" />
                  </svg>
                )}
              </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Vodeći element (zastava, ikonica) dobija fiksan okvir. SVG bez zadate veličine inače popuni
// ceo red i izgura tekst u tri reda.
const levo: CSSProperties = {
  display: "inline-flex", alignItems: "center", justifyContent: "center",
  width: 16, height: 16, flexShrink: 0, lineHeight: 1,
};
const trigger: CSSProperties = {
  display: "flex", alignItems: "center", gap: 8, textAlign: "left", cursor: "pointer",
};
const triggerLabel: CSSProperties = {
  // Without minWidth 0 a flex item never gets narrower than its text, so the ellipsis never comes and a long label
  // widens the column the control stands in instead.
  flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 8,
  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
};
const panel: CSSProperties = {
  // The list may be wider than the control, because a label is not allowed to be cut: two document
  // types or two articles often differ only at the end of the name, and a clipped end makes them one
  // entry the reader has to guess between. It never grows past the screen.
  position: "absolute", zIndex: 30, top: "calc(100% + 4px)", left: 0,
  minWidth: "100%", width: "max-content", maxWidth: "min(520px, 90vw)",
  background: "var(--surface)", border: "1px solid var(--border)",
  borderRadius: "var(--r-sm)", boxShadow: "var(--shadow-2)",
  padding: 4, maxHeight: 320, overflowY: "auto",
};
const groupHead: CSSProperties = {
  padding: "6px 10px 3px", fontSize: 10.5, fontWeight: 700, letterSpacing: 0.4,
  textTransform: "uppercase", color: "var(--ink-4)",
};
const option: CSSProperties = {
  display: "flex", gap: 10, alignItems: "flex-start", width: "100%", textAlign: "left",
  // A long label wraps rather than being cut; the panel above already grew to fit what it could.
  whiteSpace: "normal", overflowWrap: "anywhere", lineHeight: 1.35,
  padding: "8px 10px", borderRadius: 6, border: "none", cursor: "pointer",
  fontSize: 13, color: "var(--ink)",
};
